.root {
  display: flex;
  height: 100%;
  min-height: 0;
  flex-direction: column;
  color: var(--arc-foreground);
  font-family: var(--arc-font-body);
  font-size: var(--arc-text-sm);
  letter-spacing: var(--arc-tracking-body);
  line-height: var(--arc-leading-body);
}

.viewport { position: relative; display: flex; min-height: 0; flex: 1; flex-direction: column; }
.scroller { min-height: 0; flex: 1; overflow-x: hidden; overflow-y: auto; overscroll-behavior: contain; scrollbar-width: thin; scrollbar-gutter: stable; }
/* The content springs up from where it was when the thread grows; the clip keeps that travel out of the scroll range. */
.track { overflow: clip; }
.content { padding: 12px 16px 14px; will-change: transform; }

.list { display: flex; flex-direction: column; margin: 0; padding: 0; list-style: none; }

.day { display: flex; align-items: center; gap: 12px; padding: 20px 4px 4px; color: var(--arc-text-muted); font-size: var(--arc-text-xs); font-weight: 500; }
.day::before, .day::after { height: 1px; flex: 1; background: var(--arc-border-subtle); content: ""; }
.day:first-child { padding-top: 4px; }
.day > span:empty::before { content: "\00a0"; }

.row { position: relative; display: flex; align-items: flex-end; gap: 8px; margin-top: 2px; }
.row[data-first] { margin-top: 14px; }
.day + .row { margin-top: 10px; }
.row[data-mine] { justify-content: flex-end; }

.gutter { width: 28px; flex: none; align-self: flex-end; }
.stack { display: flex; min-width: 0; max-width: min(78%, 34rem); flex-direction: column; align-items: flex-start; gap: 4px; }
.row[data-mine] .stack { align-items: flex-end; }

.meta { display: flex; align-items: baseline; gap: 6px; margin: 0 0 2px; padding: 0 12px; color: var(--arc-text-muted); font-size: var(--arc-text-xs); font-variant-numeric: tabular-nums; }
.author { color: var(--arc-text-secondary); font-weight: 500; }

.line { position: relative; display: flex; max-width: 100%; align-items: center; gap: 6px; }
/* The author's face sits in the gutter, level with the bottom of their last bubble rather than the reactions under it. */
.face { position: absolute; bottom: 0; left: -36px; display: flex; }
.row[data-mine] .line { flex-direction: row-reverse; }
.parts { display: flex; min-width: 0; flex-direction: column; align-items: flex-start; gap: 4px; }
.row[data-mine] .parts { align-items: flex-end; }

/* Bubbles in a group share a soft edge on the author's side, so a run of messages reads as one voice. */
.bubble {
  --r: 18px;
  --tight: 6px;
  max-width: 100%;
  padding: 8px 13px 9px;
  border-radius: var(--r);
  background: var(--arc-surface-muted);
  color: var(--arc-foreground);
  font-size: var(--arc-text-sm);
  overflow-wrap: anywhere;
  white-space: pre-wrap;
}
.row:not([data-mine]):not([data-first]) .bubble { border-top-left-radius: var(--tight); }
.row:not([data-mine]):not([data-last]) .bubble { border-bottom-left-radius: var(--tight); }
.row[data-mine] .bubble { background: var(--arc-accent); color: var(--arc-accent-foreground); }
:global(:root[data-theme="dark"]) .row:not([data-mine]) .bubble { background: var(--arc-surface-raised); }
.row[data-mine]:not([data-first]) .bubble { border-top-right-radius: var(--tight); }
.row[data-mine]:not([data-last]) .bubble { border-bottom-right-radius: var(--tight); }
.row[data-failed] .bubble { opacity: .6; }

.gallery { display: grid; width: min(240px, 100%); gap: 3px; grid-template-columns: 1fr; }
.gallery[data-count="2"],
.gallery[data-count="3"],
.gallery[data-count="4"] { grid-template-columns: 1fr 1fr; }
.gallery[data-count="3"] > :first-child { grid-column: span 2; aspect-ratio: 2 / 1; }
.image { position: relative; display: block; overflow: hidden; aspect-ratio: 1; border-radius: 16px; background: var(--arc-surface-muted); }
.gallery[data-count="1"] .image { aspect-ratio: 4 / 3; }
.gallery:not([data-count="1"]) .image { border-radius: 6px; }
.gallery:not([data-count="1"]) { overflow: hidden; border-radius: 16px; }
.image img { display: block; width: 100%; height: 100%; object-fit: cover; }
.more { position: absolute; inset: 0; display: grid; place-items: center; background: oklch(0% 0 0 / .45); color: #fff; font-size: var(--arc-text-lg); font-weight: 500; }

.file {
  display: flex; max-width: 100%; align-items: center; gap: 10px; padding: 9px 14px 9px 11px;
  border: 1px solid var(--arc-border); border-radius: 16px; background: var(--arc-surface); color: var(--arc-foreground); text-decoration: none;
  transition: background-color var(--arc-duration-fast) var(--arc-ease-standard);
}
.file > svg { flex: none; color: var(--arc-text-secondary); }
.fileText { display: grid; min-width: 0; }
.fileName { overflow: hidden; font-weight: 500; text-overflow: ellipsis; white-space: nowrap; }
.fileSize { color: var(--arc-text-muted); font-size: var(--arc-text-xs); font-variant-numeric: tabular-nums; }
@media (hover: hover) and (pointer: fine) { .file:hover { background: var(--arc-surface-muted); } }

/* The reaction button waits beside the bubble and shows on hover or keyboard focus. */
/* The picker positions against the whole line, so it opens over the bubble and stays inside the thread. */
.pickerWrap { position: static; flex: none; }
.reactButton {
  display: grid; width: 28px; height: 28px; place-items: center; padding: 0;
  border: 0; border-radius: var(--arc-radius-pill); background: transparent; color: var(--arc-text-muted); cursor: pointer; opacity: 0;
  -webkit-tap-highlight-color: transparent;
  transition: opacity var(--arc-duration-fast) var(--arc-ease-standard), background-color var(--arc-duration-fast) var(--arc-ease-standard), color var(--arc-duration-fast) var(--arc-ease-standard);
}
.row:hover .reactButton,
.row:focus-within .reactButton,
.pickerWrap[data-open] .reactButton { opacity: 1; }
.pickerWrap[data-open] .reactButton { background: var(--arc-surface-muted); color: var(--arc-foreground); }
@media (hover: hover) and (pointer: fine) { .reactButton:hover { background: var(--arc-surface-muted); color: var(--arc-foreground); } }
@media (hover: none) { .reactButton { opacity: .7; } }

.picker {
  position: absolute; z-index: 5; bottom: calc(100% + 6px); right: -4px;
  display: flex; gap: 2px; padding: 4px;
  border: 1px solid var(--arc-border); border-radius: var(--arc-radius-pill); background: var(--arc-surface-raised); box-shadow: var(--arc-shadow-floating);
  transform-origin: calc(100% - 18px) 100%;
}
.picker[data-mine] { right: auto; left: -4px; transform-origin: 18px 100%; }
.pickerWrap[data-below] .picker { top: calc(100% + 6px); bottom: auto; transform-origin: calc(100% - 18px) 0; }
.pickerWrap[data-below] .picker[data-mine] { transform-origin: 18px 0; }
.emoji {
  display: grid; width: 34px; height: 34px; place-items: center; padding: 0;
  border: 0; border-radius: var(--arc-radius-pill); background: transparent; cursor: pointer; font-size: 19px; line-height: 1;
  -webkit-tap-highlight-color: transparent;
  transition: background-color var(--arc-duration-fast) var(--arc-ease-standard), transform var(--arc-duration-instant) var(--arc-ease-standard);
}
.emoji:focus-visible { background: var(--arc-surface-muted); }
.emoji:active { transform: scale(.9); }
@media (hover: hover) and (pointer: fine) { .emoji:hover { background: var(--arc-surface-muted); transform: scale(1.12); } }

.reactionClip { overflow: hidden; margin: -2px -4px; padding: 2px 4px 2px 8px; }
.row[data-mine] .reactionClip { padding: 2px 8px 2px 4px; }
.reactions { display: flex; flex-wrap: wrap; gap: 4px; margin: 0; padding: 0; list-style: none; }
.row[data-mine] .reactions { justify-content: flex-end; }
.reaction {
  display: inline-flex; height: 26px; align-items: center; gap: 5px; padding: 0 9px 0 7px;
  border: 1px solid var(--arc-border-subtle); border-radius: var(--arc-radius-pill); background: var(--arc-surface); color: var(--arc-text-secondary); cursor: pointer;
  font: inherit; font-size: var(--arc-text-xs); font-weight: 500; line-height: 1; font-variant-numeric: tabular-nums;
  -webkit-tap-highlight-color: transparent;
  transition: background-color var(--arc-duration-fast) var(--arc-ease-standard), border-color var(--arc-duration-fast) var(--arc-ease-standard), color var(--arc-duration-fast) var(--arc-ease-standard), transform var(--arc-duration-instant) var(--arc-ease-standard);
}
.reaction > span:first-child { font-size: 14px; }
.reaction[aria-pressed="true"] { border-color: color-mix(in oklab, var(--arc-accent) 28%, transparent); background: color-mix(in oklab, var(--arc-accent) 8%, var(--arc-surface)); color: var(--arc-foreground); }
@media (hover: hover) and (pointer: fine) { .reaction:hover { border-color: var(--arc-border-strong); } }
.reaction:active { transform: scale(.95); }
.countRoll { display: inline-grid; overflow: hidden; font-variant-numeric: tabular-nums; }
.countRoll > span { grid-area: 1 / 1; }

.receipts { display: flex; min-height: 16px; align-items: center; gap: 6px; padding: 1px 6px 0; color: var(--arc-text-muted); font-size: var(--arc-text-xs); }
.readers { display: inline-flex; }
.reader { display: inline-flex; border-radius: 50%; box-shadow: 0 0 0 2px var(--arc-surface); }
.reader + .reader { margin-left: -4px; }
.status[data-status="failed"] { display: inline-flex; align-items: center; gap: 8px; color: var(--arc-danger); }
.retry {
  display: inline-flex; align-items: center; gap: 4px; padding: 2px 8px;
  border: 1px solid var(--arc-border); border-radius: var(--arc-radius-pill); background: var(--arc-surface); color: var(--arc-foreground); cursor: pointer;
  font: inherit; font-weight: 500; line-height: 16px;
  -webkit-tap-highlight-color: transparent;
  transition: background-color var(--arc-duration-fast) var(--arc-ease-standard), border-color var(--arc-duration-fast) var(--arc-ease-standard);
}
.retry > svg { color: var(--arc-text-secondary); }
@media (hover: hover) and (pointer: fine) { .retry:hover { background: var(--arc-surface-muted); border-color: var(--arc-border-strong); } }

.avatar { display: inline-grid; flex: none; overflow: hidden; place-items: center; border-radius: 50%; background: var(--arc-surface-muted); color: var(--arc-text-secondary); font-weight: 500; }
.avatar img { width: 100%; height: 100%; object-fit: cover; }

.typing { display: grid; height: 36px; box-sizing: border-box; place-items: center; padding: 0 14px; }
.dots { display: inline-flex; gap: 4px; }
.dots i { width: 6px; height: 6px; border-radius: 50%; background: var(--arc-text-muted); animation: breathe 1.2s var(--arc-ease-in-out) infinite; }
.dots i:nth-child(2) { animation-delay: .16s; }
.dots i:nth-child(3) { animation-delay: .32s; }
@keyframes breathe {
  0%, 60%, 100% { opacity: .35; transform: translateY(0); }
  30% { opacity: 1; transform: translateY(-3px); }
}
@media (prefers-reduced-motion: reduce) { .dots i { animation: none; opacity: .6; } }

.pill {
  position: absolute; z-index: 6; bottom: 12px; left: 50%; translate: -50% 0;
  display: inline-flex; height: 32px; align-items: center; gap: 6px; padding: 0 14px 0 11px;
  border: 1px solid var(--arc-border); border-radius: var(--arc-radius-pill); background: var(--arc-surface-raised); box-shadow: var(--arc-shadow-floating); color: var(--arc-foreground); cursor: pointer;
  font: inherit; font-size: var(--arc-text-xs); font-weight: 500; white-space: nowrap;
  -webkit-tap-highlight-color: transparent;
}
.pill > span { display: inline-flex; gap: .3em; }
.pill > .pillIcon { display: inline-grid; place-items: center; }
.pill:active { scale: .97; }

/* Composer. */
.composer { display: grid; padding: 10px 12px 12px; border-top: 1px solid var(--arc-border-subtle); transition: background-color var(--arc-duration-fast) var(--arc-ease-standard); }
.composer[data-dragging] { background: var(--arc-accent-subtle); }
.trayClip { overflow: hidden; }
.tray { display: flex; gap: 8px; margin: 0; padding: 8px 8px 10px 4px; overflow-x: auto; list-style: none; scrollbar-width: none; }
.pending { position: relative; flex: none; }
.pendingImage { display: block; width: 56px; height: 56px; border-radius: 12px; object-fit: cover; }
.pendingFile { display: grid; width: 168px; height: 56px; box-sizing: border-box; align-content: center; column-gap: 8px; padding: 0 12px; border: 1px solid var(--arc-border); border-radius: 12px; background: var(--arc-surface); grid-template-columns: auto minmax(0, 1fr); }
.pendingFile > svg { grid-row: span 2; align-self: center; color: var(--arc-text-secondary); }
.pendingName { overflow: hidden; font-size: var(--arc-text-xs); font-weight: 500; text-overflow: ellipsis; white-space: nowrap; }
.pendingSize { color: var(--arc-text-muted); font-size: var(--arc-text-xs); font-variant-numeric: tabular-nums; }
.remove {
  position: absolute; top: -6px; right: -6px; display: grid; width: 20px; height: 20px; place-items: center; padding: 0;
  border: 2px solid var(--arc-surface); border-radius: 50%; background: var(--arc-foreground); color: var(--arc-background); cursor: pointer;
}

.inputRow { display: flex; align-items: flex-end; gap: 6px; }
.attach {
  display: grid; width: 40px; height: 40px; flex: none; place-items: center; margin-bottom: 1px; padding: 0;
  border: 0; border-radius: var(--arc-radius-pill); background: transparent; color: var(--arc-text-secondary); cursor: pointer;
  -webkit-tap-highlight-color: transparent;
  transition: background-color var(--arc-duration-fast) var(--arc-ease-standard), color var(--arc-duration-fast) var(--arc-ease-standard), transform var(--arc-duration-instant) var(--arc-ease-standard);
}
.attach:active { transform: scale(.94); }
@media (hover: hover) and (pointer: fine) { .attach:hover { background: var(--arc-surface-muted); color: var(--arc-foreground); } }

/* The field and the send button share one rounded box; the button rides the bottom edge as the text grows. */
.box {
  position: relative; display: flex; min-width: 0; flex: 1; align-items: flex-end;
  border: 1px solid var(--arc-border); border-radius: 21px; background: var(--arc-surface);
  transition: border-color var(--arc-duration-fast) var(--arc-ease-standard), background-color var(--arc-duration-fast) var(--arc-ease-standard);
}
.box:focus-within { border-color: var(--arc-border-strong); }
.composer[data-dragging] .box { border-color: var(--arc-accent); border-style: dashed; }
.field { position: relative; min-width: 0; flex: 1; overflow: hidden; border-radius: 20px; }
.mirror,
.textarea {
  box-sizing: border-box; width: 100%; margin: 0; padding: 9px 6px 9px 16px; border: 0;
  font: inherit; font-size: var(--arc-text-base); letter-spacing: var(--arc-tracking-body); line-height: 22px;
  overflow-wrap: break-word; white-space: pre-wrap; word-break: break-word;
}
.mirror { position: absolute; top: 0; left: 0; visibility: hidden; pointer-events: none; }
.textarea { position: absolute; inset: 0; height: 100%; display: block; resize: none; overflow-y: auto; background: transparent; color: var(--arc-foreground); outline: none; scrollbar-width: none; }
.textarea::placeholder { color: var(--arc-text-muted); }

.send {
  position: relative; display: grid; width: 32px; height: 32px; flex: none; place-items: center; margin: 0 4px 4px 0; overflow: hidden; padding: 0;
  border: 0; border-radius: 50%; background: transparent; color: var(--arc-text-muted); cursor: default;
  -webkit-tap-highlight-color: transparent;
  transition: background-color var(--arc-duration-standard) var(--arc-ease-standard), color var(--arc-duration-standard) var(--arc-ease-standard), transform var(--arc-duration-instant) var(--arc-ease-standard);
}
.send[data-ready] { background: var(--arc-accent); color: var(--arc-accent-foreground); cursor: pointer; }
.send[data-ready]:active { transform: scale(.92); }
.sendIcon { display: grid; grid-area: 1 / 1; place-items: center; }

.srOnly { position: absolute; width: 1px; height: 1px; overflow: hidden; clip-path: inset(50%); white-space: nowrap; }

@media (max-width: 420px) {
  .content { padding-inline: 12px; }
  .stack { max-width: 84%; }
}
/* PROMOTION U14 (additive) : envoi en attente, bouton Stop pendant une génération, erreur d'envoi qui garde le brouillon. */
.send[data-pending] { cursor: progress; opacity: .7; }
.send[data-stop] { background: var(--arc-foreground); color: var(--arc-background); cursor: pointer; }
.composerError { margin: 6px 2px 0; color: var(--arc-danger); font-size: 12px; line-height: 16px; }

:global([data-motion="reduced"]) .root *, :global([data-motion="reduced"]) .root {transition:none;}
